import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { Pitch } from '@/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, DollarSign, Lock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Clock, 
  AlertCircle, 
  AlertTriangle,
  Calendar,
  TrendingUp,
  FileText,
  Eye,
  Upload,
  CheckCircle,
  XCircle,
  Users,
  Building2
} from 'lucide-react';
import { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';

interface Props {
  pitch: Pitch;
}

// Extended milestone type to include timeline and funding split
interface ExtendedMilestone {
  id: string;
  title: string;
  trancheAmount: number;
  companyAmount: number;
  vvAmount: number;
  completed: boolean;
  approved: boolean;
  dueDate?: string; // Days from LOI
  gracePeriod?: number; // Days
  penaltyRate?: number; // % per day
  deliverables?: string[];
  deliveredAt?: string;
  approvedAt?: string;
}

export default function FundingPanel({ pitch }: Props) {
  const { user } = useAuth();
  const { completeMilestone, approveMilestone } = useData();
  const { toast } = useToast();

  const isFounder = user?.role === 'founder' && user.id === pitch.founderId;
  const isSenior = user?.role === 'senior_analyst';
  
  // ORIGINAL CODE - Keep exactly as is
  const totalFunding = pitch.milestones.reduce((s, m) => s + m.trancheAmount, 0);
  const released = totalFunding - pitch.escrowBalance;

  // OPTION 4: Hybrid Model Numbers
  const RETAINER = 50000; // Company pays upfront
  const COMPANY_PER_MILESTONE = 100000; // Company pays per milestone
  const VV_PER_MILESTONE = 400000; // V&V funds per milestone
  const TOTAL_PER_MILESTONE = COMPANY_PER_MILESTONE + VV_PER_MILESTONE; // 500,000
  
  const TOTAL_COMPANY_CONTRIBUTION = RETAINER + (COMPANY_PER_MILESTONE * 3); // 50k + 300k = 350k
  const TOTAL_VV_CONTRIBUTION = VV_PER_MILESTONE * 3; // 1,200,000
  const TOTAL_FUNDING = TOTAL_COMPANY_CONTRIBUTION + TOTAL_VV_CONTRIBUTION; // 1,550,000

  // NEW: Add timeline data to milestones
  const loiDate = (pitch as any).loiDate || new Date().toISOString();
  const loiDateObj = new Date(loiDate);

  // Enhance milestones with timeline data and funding split
  const enhancedMilestones = pitch.milestones.map((m, index) => {
    const daysFromLOI = index === 0 ? 60 : index === 1 ? 90 : 180;
    
    return {
      ...m,
      companyAmount: COMPANY_PER_MILESTONE,
      vvAmount: VV_PER_MILESTONE,
      dueDate: new Date(loiDateObj.getTime() + daysFromLOI * 24 * 60 * 60 * 1000).toISOString(),
      gracePeriod: 7,
      penaltyRate: index === 0 ? 2 : index === 1 ? 3 : 5,
      deliverables: [
        'Working MVP for testing',
        'Documentation',
        'Deployment guide'
      ]
    };
  });

  // Helper function to calculate days remaining
  const getDaysRemaining = (dueDate: string): number => {
    const due = new Date(dueDate).getTime();
    const now = new Date().getTime();
    const diff = due - now;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  // Helper function to check if milestone is overdue
  const isOverdue = (milestone: any): boolean => {
    if (milestone.approved || !milestone.dueDate) return false;
    return getDaysRemaining(milestone.dueDate) < 0;
  };

  return (
    <div className="space-y-6">
      {/* ===== ORIGINAL HEADER - UNCHANGED ===== */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Funding & Milestones</h3>
      </div>

      {/* ===== OPTION 4: Funding Summary Header ===== */}
      {(pitch as any).loiDate && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-700">
                  LOI Signed: {new Date((pitch as any).loiDate).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Building2 className="h-4 w-4 text-green-600" />
                  <span className="text-green-700">Company: KES {TOTAL_COMPANY_CONTRIBUTION.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4 text-purple-600" />
                  <span className="text-purple-700">V&V: KES {TOTAL_VV_CONTRIBUTION.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== ORIGINAL STAT CARDS - UPDATED with Option 4 numbers ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <StatCard 
          label="Total Funding" 
          value={`KES ${TOTAL_FUNDING.toLocaleString()}`} 
          subtext="Company + V&V"
        />
        <StatCard 
          label="Company Contribution" 
          value={`KES ${TOTAL_COMPANY_CONTRIBUTION.toLocaleString()}`} 
          subtext="22.6% of total"
          color="text-green-600"
        />
        <StatCard 
          label="V&V Contribution" 
          value={`KES ${TOTAL_VV_CONTRIBUTION.toLocaleString()}`} 
          subtext="77.4% of total"
          color="text-purple-600"
        />
        <StatCard 
          label="In Escrow" 
          value={`KES ${pitch.escrowBalance.toLocaleString()}`} 
          subtext="Pending release"
        />
      </div>

      {/* ===== NEW: Progress Bar with split ===== */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between mb-4">
            <span className="text-sm font-medium">Funding Progress</span>
            <span className="text-sm text-muted-foreground">
              {Math.round((released / TOTAL_FUNDING) * 100)}% Complete
            </span>
          </div>
          <Progress value={(released / TOTAL_FUNDING) * 100} className="h-2 mb-4" />
          
          {/* Funding split visualization */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Company Paid</p>
              <p className="text-lg font-semibold text-green-600">
                KES {Math.min(TOTAL_COMPANY_CONTRIBUTION, (released * (TOTAL_COMPANY_CONTRIBUTION / TOTAL_FUNDING))).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">of KES {TOTAL_COMPANY_CONTRIBUTION.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">V&V Funded</p>
              <p className="text-lg font-semibold text-purple-600">
                KES {Math.min(TOTAL_VV_CONTRIBUTION, (released * (TOTAL_VV_CONTRIBUTION / TOTAL_FUNDING))).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">of KES {TOTAL_VV_CONTRIBUTION.toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===== NEW: Warning for upcoming deadlines ===== */}
      {enhancedMilestones.some(m => {
        if (m.approved) return false;
        const days = getDaysRemaining(m.dueDate);
        return days <= 7 && days > 0;
      }) && (
        <Alert className="bg-yellow-50 border-yellow-200">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-700">
            Some milestones are due within 7 days. Please ensure timely delivery.
          </AlertDescription>
        </Alert>
      )}

      {/* ===== ORIGINAL MILESTONE TRACKER - ENHANCED with Option 4 ===== */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium">Milestone Tracker</h4>
        <div className="space-y-2">
          {enhancedMilestones.map((m, index) => {
            const daysRemaining = getDaysRemaining(m.dueDate);
            const overdue = isOverdue(m);
            
            return (
              <div key={m.id} className="border rounded-md p-4">
                {/* ORIGINAL milestone layout - preserved */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                      m.approved ? 'bg-success/10' : m.completed ? 'bg-warning/10' : 'bg-muted'
                    }`}>
                      {m.approved ? (
                        <Check className="h-4 w-4 text-success" />
                      ) : m.completed ? (
                        <DollarSign className="h-4 w-4 text-warning" />
                      ) : (
                        <Lock className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{m.title}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <p className="text-xs text-green-600">
                          Company: KES {m.companyAmount.toLocaleString()}
                        </p>
                        <p className="text-xs text-purple-600">
                          V&V: KES {m.vvAmount.toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Total: KES {m.trancheAmount.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.approved ? (
                      <Badge className="bg-success/10 text-success">Released</Badge>
                    ) : m.completed ? (
                      <>
                        <Badge variant="outline" className="bg-warning/10 text-warning">Awaiting Approval</Badge>
                        {isSenior && (
                          <Button size="sm" onClick={() => approveMilestone(pitch.id, m.id)}>
                            Approve & Release
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <Badge variant="outline">Pending</Badge>
                        {isFounder && (
                          <Button size="sm" variant="outline" onClick={() => completeMilestone(pitch.id, m.id)}>
                            Mark Complete
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* NEW: Timeline info for this milestone */}
                <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Due Date</p>
                    <p className={`text-sm font-medium flex items-center gap-1 ${
                      overdue && !m.approved ? 'text-red-600' : ''
                    }`}>
                      <Calendar className="h-3 w-3" />
                      {new Date(m.dueDate).toLocaleDateString()}
                      {!m.approved && (
                        <span className={`text-xs ml-2 ${
                          overdue ? 'text-red-600' : daysRemaining <= 7 ? 'text-yellow-600' : 'text-muted-foreground'
                        }`}>
                          ({daysRemaining < 0 ? `${Math.abs(daysRemaining)} days overdue` : `${daysRemaining} days left`})
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Deliverables</p>
                    <ul className="text-xs list-disc list-inside">
                      {m.deliverables?.map((d, i) => (
                        <li key={i} className="text-muted-foreground">{d}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* NEW: Funding split visualization for this milestone */}
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="p-2 bg-green-50 rounded">
                    <p className="text-xs text-green-700">Company Payment</p>
                    <p className="text-sm font-semibold text-green-600">KES {m.companyAmount.toLocaleString()}</p>
                    <p className="text-xs text-green-600">{Math.round((m.companyAmount / m.trancheAmount) * 100)}% of milestone</p>
                  </div>
                  <div className="p-2 bg-purple-50 rounded">
                    <p className="text-xs text-purple-700">V&V Funding</p>
                    <p className="text-sm font-semibold text-purple-600">KES {m.vvAmount.toLocaleString()}</p>
                    <p className="text-xs text-purple-600">{Math.round((m.vvAmount / m.trancheAmount) * 100)}% of milestone</p>
                  </div>
                </div>

                {/* NEW: Penalty warning if overdue */}
                {overdue && !m.approved && (
                  <div className="mt-2 p-2 bg-red-50 rounded flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-red-700">Overdue - Penalty Applied</p>
                      <p className="text-xs text-red-600">
                        {m.penaltyRate}% penalty per day on company portion. Maximum 50% of milestone.
                      </p>
                    </div>
                  </div>
                )}

                {/* NEW: Action buttons for deliverables */}
                {isFounder && !m.completed && !m.approved && (
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1">
                      <Upload className="h-3 w-3 mr-1" />
                      Submit Deliverables
                    </Button>
                  </div>
                )}

                {(isSenior || isFounder) && m.completed && !m.approved && (
                  <div className="mt-3 p-2 bg-blue-50 rounded">
                    <p className="text-xs text-blue-700 flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      Deliverables submitted. Awaiting review.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== NEW: Completion message ===== */}
      {enhancedMilestones.every(m => m.approved) && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-green-700">All Milestones Complete!</h3>
            <p className="text-sm text-green-600">
              Total Company Contribution: KES {TOTAL_COMPANY_CONTRIBUTION.toLocaleString()}<br />
              Total V&V Funding: KES {TOTAL_VV_CONTRIBUTION.toLocaleString()}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===== UPDATED STATCARD with optional color and subtext =====
function StatCard({ label, value, subtext, color }: { 
  label: string; 
  value: string; 
  subtext?: string;
  color?: string;
}) {
  return (
    <div className="border rounded-md p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold mt-1 ${color || ''}`}>{value}</p>
      {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
    </div>
  );
}